/**
 * The loading splash on the signed-in screens (Sep 11 2026). Thomas: "a
 * better splash image when Enterprise is loading ... ideally to match the one
 * we are using on P3 mobile redesign at initial load".
 *
 * It redraws the redesign's launch screen (p3-mobile ios/P3/LaunchScreen.
 * storyboard and Images.xcassets/Splash) for a browser, rather than cropping
 * the phone-shaped image: the maroon wash over the blurred campus photograph,
 * and the white stacked wordmark in the middle. The wash is measured from
 * Splash@3x.png down a strip clear of the lettering (base.css 6.18). The
 * wordmark is the rail lockup's own paths (public/brand/p3-logo-white.svg)
 * with each line centered on the widest, as the mobile splash sets it, drawn
 * inline so the splash never waits on a download.
 *
 * Both waits on a signed-in screen render it, the session check and then the
 * institution's figures, so the two read as one screen. The failures keep
 * their cards: a failure has to be read, not waited through.
 */

export function LoadingSplash({ label }: { label: string }) {
  return (
    <div data-surface="enterprise" className="e-splash" role="status" aria-busy="true">
      <span className="e-splash-photo" aria-hidden="true" />
      <P3Wordmark className="e-splash-mark" />
      <span className="e-splash-bar" aria-hidden="true">
        <i />
      </span>
      <span className="e-sr-only">{label}</span>
    </div>
  );
}

/** "The Pulse of Perseverance Project", four lines centered on PERSEVERANCE. */
function P3Wordmark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="466 21 484 218"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {/* THE */}
      <g transform="translate(212.85 0)">
        <polygon points="459 25 459 28.5 470 28.5 470 56.5 473.7 56.5 473.7 28.5 484.8 28.5 484.8 25 459 25" />
        <polygon points="504.6 25 504.6 39 487.6 39 487.6 25 484 25 484 56.5 487.6 56.5 487.6 42.5 504.6 42.5 504.6 56.5 508.3 56.5 508.3 25 504.6 25" />
        <polygon points="515.6 53 515.6 42.4 525.6 42.4 527.4 38.9 515.6 38.9 515.6 28.5 528.3 28.5 530 25 512 25 512 56.5 529 56.5 530.8 53 515.6 53" />
      </g>
      {/* PULSE OF */}
      <g transform="translate(112.15 0)">
        <path d="M470,64h19.7c9.6,0,16.3,6.7,16.3,16.6s-6.7,16.8-16.3,16.8h-10.4v17.6h-9.3v-51ZM488,89.4c5.4,0,8.6-3.5,8.6-8.8s-3.2-8.6-8.4-8.6h-8.8v17.4h8.6Z" />
        <path d="M509.9,64h9.3v32.7c0,6.8,4.5,10.7,11.3,10.7s11.4-3.9,11.4-10.8v-32.7h9.3v32.7c0,11.4-8.3,19.2-20.6,19.2s-20.6-7.8-20.6-19.2v-32.6h-.1Z" />
        <path d="M557,64h9.3v43.1h22.1l-4.1,8h-27.3v-51.1Z" />
        <path d="M595.2,101.9s5.5,5.4,12.7,5.4c3.9,0,7.4-2,7.4-6.2,0-9.1-24.1-7.6-24.1-23.2,0-8.5,7.3-14.8,17.1-14.8s15.1,5.3,15.1,5.3l-3.9,7.7s-4.9-4.5-11.3-4.5c-4.3,0-7.7,2.5-7.7,6.1,0,9.1,24,6.8,24,23.2,0,8.1-6.2,15-16.8,15-8.7,0-14.5-4.1-16.6-6l4.1-8Z" />
        <path d="M629.1,64.4h30.6l-4.1,8h-17.2v13.4h17.2l-4.1,8h-13.1v13.7h22.4l-4.1,8h-27.6v-51.1h0Z" />
        <path d="M685.3,83.9c-8.9,0-15.9,7-15.9,16s7.1,16.5,15.9,16.5,15.9-7.4,15.9-16.5c.1-8.9-6.9-16-15.9-16ZM685.3,87.5c6.8,0,12.2,5.5,12.2,12.5s-5.4,12.9-12.2,12.9-12.2-5.7-12.2-12.9,5.4-12.5,12.2-12.5Z" />
        <polygon points="719.4 87.9 721.2 84.4 704.4 84.4 704.4 115.9 708 115.9 708 102 717.6 102 719.4 98.5 708 98.5 708 87.9 719.4 87.9" />
      </g>
      {/* PERSEVERANCE, the widest line, where it sits in the lockup */}
      <g>
        <path d="M470,123.4h19.7c9.6,0,16.3,6.7,16.3,16.6s-6.7,16.8-16.3,16.8h-10.4v17.6h-9.3v-51ZM488,148.8c5.4,0,8.6-3.5,8.6-8.8s-3.2-8.6-8.4-8.6h-8.8v17.4h8.6Z" />
        <path d="M509.9,123.4h30.6l-4.1,8h-17.2v13.4h17.2l-4.1,8h-13.1v13.7h22.4l-4.1,8h-27.6v-51.1h0Z" />
        <path d="M563.4,147.3c5,0,8.1-2.9,8.1-8.1,0-4.9-2.1-7.9-9.3-7.9h-7.1v43.1h-9.3v-51h16.5c5.5,0,8.1.4,10.3,1.4,5.1,2.3,8.3,7.2,8.3,14,0,6.1-3.2,11.8-8.6,14v.1s.7.7,1.7,2.5l10.5,19.1h-10.4l-14.4-27.1h3.7v-.1Z" />
        <path d="M589.5,161.3s5.5,5.4,12.7,5.4c3.9,0,7.4-2,7.4-6.2,0-9.1-24.1-7.6-24.1-23.2,0-8.5,7.3-14.8,17.1-14.8s15.1,5.3,15.1,5.3l-3.9,7.7s-4.9-4.5-11.3-4.5c-4.3,0-7.7,2.5-7.7,6.1,0,9.1,24,6.8,24,23.2,0,8.1-6.2,15-16.8,15-8.7,0-14.5-4.1-16.6-6l4.1-8Z" />
        <path d="M623.2,123.4h30.6l-4.1,8h-17.2v13.4h17.2l-4.1,8h-13.1v13.7h22.4l-4.1,8h-27.6v-51.1h0Z" />
        <path d="M653.5,123.4h10l11.2,33.3c1,2.9,1.9,7.5,1.9,7.5h.1c.1,0,.9-4.5,1.9-7.5l11.3-33.3h9.9l-18.6,51.1h-9.2l-18.5-51.1Z" />
        <path d="M700,123.4h30.6l-4.1,8h-17.2v13.4h17.2l-4.1,8h-13.1v13.7h22.4l-4.1,8h-27.6v-51.1h0Z" />
        <path d="M753,147.3c5,0,8.1-2.9,8.1-8.1,0-4.9-2.1-7.9-9.3-7.9h-7.1v43.1h-9.3v-51h16.5c5.5,0,8.1.4,10.3,1.4,5.1,2.3,8.3,7.2,8.3,14,0,6.1-3.2,11.8-8.6,14v.1s.7.7,1.7,2.5l10.5,19.1h-10.4l-14.4-27.1h3.7v-.1Z" />
        <path d="M782.2,174.4h-9.6l18-51.1h9.7l18,51.1h-9.6l-13.3-40.4-13.2,40.4Z" />
        <path d="M818.3,123.4h9.3l19.3,29.5c1.9,2.9,4.2,7.9,4.2,7.9h.1s-.5-4.9-.5-7.9v-29.5h9.2v51h-9.2l-19.3-29.4c-1.9-2.9-4.2-7.9-4.2-7.9h-.1s.5,4.9.5,7.9v29.4h-9.3v-51Z" />
        <path d="M890.1,122.5c12.4,0,18.8,7,18.8,7l-4.5,6.9s-5.8-5.4-14-5.4c-10.8,0-17.1,8.1-17.1,17.6s6.5,18.3,17.2,18.3c8.8,0,14.8-6.5,14.8-6.5l4.9,6.7s-7.1,8.3-20.1,8.3c-15.6,0-26.4-11.6-26.4-26.6.2-14.9,11.4-26.3,26.4-26.3Z" />
        <path d="M914.9,123.4h30.6l-4.1,8h-17.2v13.4h17.2l-4.1,8h-13.1v13.7h22.4l-4.1,8h-27.6v-51.1h0Z" />
      </g>
      {/* PROJECT */}
      <g transform="translate(93 0)">
        <path d="M470,182.8h19.7c9.6,0,16.3,6.7,16.3,16.6s-6.7,16.8-16.3,16.8h-10.4v17.6h-9.3v-51ZM488,208.2c5.4,0,8.6-3.5,8.6-8.8s-3.2-8.6-8.4-8.6h-8.8v17.4h8.6Z" />
        <path d="M527.5,206.7c5,0,8.1-2.9,8.1-8.1,0-4.9-2.1-7.9-9.3-7.9h-7.1v43.2h-9.3v-51.1h16.5c5.5,0,8.1.4,10.3,1.4,5.1,2.3,8.3,7.2,8.3,14,0,6.1-3.2,11.8-8.6,14v.1s.7.7,1.7,2.5l10.5,19.1h-10.4l-14.4-27.1h3.7v-.1Z" />
        <path d="M573.3,181.9c15,0,26.4,11.4,26.4,26s-11.4,26.8-26.4,26.8-26.4-11.7-26.4-26.8,11.4-26,26.4-26ZM573.3,226.2c9.4,0,16.8-7.8,16.8-18.3s-7.5-17.6-16.8-17.6-16.8,7.5-16.8,17.6c0,10.6,7.5,18.3,16.8,18.3Z" />
        <path d="M600.8,215.8h9.2v2.4c0,6,3.3,8.1,6.8,8.1s6.5-2.1,6.5-7.6v-27.8h-13.2l4.1-8h18.4v36c0,11.1-8.1,16-16,16s-15.8-5-15.8-16v-3.1h0Z" />
        <path d="M638.6,182.8h30.6l-4.1,8h-17.2v13.4h17.2l-4.1,8h-13.1v13.7h22.4l-4.1,8h-27.6v-51.1Z" />
        <path d="M696.5,181.9c12.4,0,18.8,7,18.8,7l-4.5,6.9s-5.8-5.4-14-5.4c-10.8,0-17.1,8.1-17.1,17.6s6.5,18.3,17.2,18.3c8.8,0,14.8-6.5,14.8-6.5l4.9,6.7s-7,8.3-20.1,8.3c-15.6,0-26.4-11.6-26.4-26.6.2-14.9,11.4-26.3,26.4-26.3Z" />
        <path d="M733.5,190.8h-16.8v-8h42.8v8h-16.8v43.1h-9.3v-43.1h.1Z" />
      </g>
    </svg>
  );
}
